import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { usePtSettings, useUpdatePtSettings } from '../../hooks/usePressureTesting';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminPt, PT_CERT_FORMAT_LABEL, PT_TICKET_MODE_LABEL } from '../../lib/pressure-testing';
import { PT_CERT_FORMATS, PT_TICKET_MODES, type PtCertFormat, type PtSettingsUpdate, type PtTicketMode } from '../../types/pressure-testing';
import { PtRateCard } from '../pressure-testing/PtRateCard';
import { PtCustomerTicketModes } from '../pressure-testing/PtCustomerTicketModes';

const inputCls =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed';

/** "60, 30, 7" → [60, 30, 7]; null when any part is not a whole number. The API validates the range. */
function parseWarnDays(text: string): number[] | null {
  const parts = text.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  const nums = parts.map(Number);
  return nums.every(Number.isInteger) ? nums : null;
}

/*
 * Settings → Operations → Pressure Testing (GET/PUT /pressure-testing/settings,
 * fieldiq-api#907).
 *
 * A tenant with no row reads the API's defaults with `configured: false`, and
 * the page says so: those values are in effect but nobody chose them.
 *
 * Only the fields that changed are sent. The API's PUT keeps whatever it is
 * not handed, so echoing an unchanged prefix back is a write nobody made.
 *
 * Writes are PT_ADMIN on the API (admin, office_staff — owner is NOT on the
 * list); everyone else reads the values with the controls disabled.
 *
 * ABSA outside Alberta SAVES and answers a warning (decided API-side,
 * 2026-10-05: warn, do not refuse); the warning is shown as the API wrote it.
 *
 * PT-5 (fieldiq-api#932) adds two sections under the one Pressure Testing tab,
 * switched by `?tab=` so the workspace keeps one entry for the vertical:
 *   General                       the fields above, plus the default ticket mode
 *   Rate card                     PtRateCard — PT's own rates
 *   Customer ticket preferences   PtCustomerTicketModes — per job or per day
 * The strip sits in the body under the workspace's own tabs, underlined and
 * smaller, so it reads as nested rather than as a peer.
 */
const SECTIONS = [
  { key: 'general', label: 'General' },
  { key: 'rates', label: 'Rate card' },
  { key: 'customers', label: 'Customer ticket preferences' },
] as const;
type SectionKey = (typeof SECTIONS)[number]['key'];

export default function PressureTestingSettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('tab');
  const section: SectionKey = SECTIONS.some((x) => x.key === raw) ? (raw as SectionKey) : 'general';
  const settingsQuery = usePtSettings();
  const pick = (key: SectionKey) => {
    const p = new URLSearchParams(searchParams);
    if (key === 'general') p.delete('tab');
    else p.set('tab', key);
    setSearchParams(p, { replace: true });
  };
  return (
    <div className="min-h-full">
      <div role="tablist" aria-label="Pressure testing settings" className="flex gap-5 overflow-x-auto border-b border-fieldiq-border px-6 pt-3">
        {SECTIONS.map((x) => (
          <button
            key={x.key}
            type="button"
            role="tab"
            aria-selected={section === x.key}
            onClick={() => pick(x.key)}
            data-testid={`pt-settings-tab-${x.key}`}
            className={`whitespace-nowrap border-b-2 px-1 pb-2 text-xs font-bold transition ${
              section === x.key ? 'border-fi-amber text-fieldiq-text' : 'border-transparent text-fieldiq-muted hover:text-fieldiq-text'
            }`}
          >
            {x.label}
          </button>
        ))}
      </div>
      {section === 'general' ? (
        <PtGeneralSettings />
      ) : section === 'rates' ? (
        <div className="px-6 py-4">
          <PtRateCard />
        </div>
      ) : (
        <div className="px-6 py-4">
          <PtCustomerTicketModes companyDefault={settingsQuery.data?.data?.default_ticket_mode ?? null} />
        </div>
      )}
    </div>
  );
}

function PtGeneralSettings() {
  const { user } = useAuth();
  const canWrite = canAdminPt(user?.role);
  const query = usePtSettings();
  const save = useUpdatePtSettings();
  const settings = query.data?.data ?? null;
  const failure = queryLoadFailure(query);

  const [prefix, setPrefix] = useState('');
  const [format, setFormat] = useState<PtCertFormat>('generic');
  const [warnDays, setWarnDays] = useState('');
  const [ticketMode, setTicketMode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const initialWarn = settings ? settings.cal_warn_days.join(', ') : '';
  useEffect(() => {
    if (settings) {
      setPrefix(settings.cert_prefix);
      setFormat((PT_CERT_FORMATS as readonly string[]).includes(settings.cert_format) ? (settings.cert_format as PtCertFormat) : 'generic');
      setWarnDays(settings.cal_warn_days.join(', '));
      setTicketMode(settings.default_ticket_mode ?? '');
    }
  }, [settings]);

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" aria-label="Loading pressure testing settings" />
      </div>
    );
  }
  if (failure || !settings) {
    return (
      <div className="px-6 py-4">
        <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="pressure testing settings" />
      </div>
    );
  }

  const body: PtSettingsUpdate = {};
  if (prefix.trim() !== settings.cert_prefix) body.cert_prefix = prefix.trim();
  if (format !== settings.cert_format) body.cert_format = format;
  if (ticketMode && ticketMode !== (settings.default_ticket_mode ?? '')) body.default_ticket_mode = ticketMode as PtTicketMode;
  const warnChanged = warnDays.replace(/\s/g, '') !== initialWarn.replace(/\s/g, '');
  const dirty = Object.keys(body).length > 0 || warnChanged;

  const onSave = () => {
    setError(null);
    setWarnings([]);
    if (warnChanged) {
      const days = parseWarnDays(warnDays);
      if (!days) return setError('Warning days must be whole numbers separated by commas, e.g. 60, 30, 7.');
      body.cal_warn_days = days;
    }
    save.mutate(body, {
      onSuccess: (res) => {
        setWarnings(res.warnings);
        toast.success('Pressure testing settings saved');
      },
      onError: (err: any) => setError(err?.response?.data?.error || 'The settings could not be saved.'),
    });
  };

  return (
    <div className="min-h-full">
      {/* The Operations workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-4">
        {canWrite ? (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onSave}
              disabled={!dirty || save.isPending}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                dirty ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
              }`}
            >
              {save.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
              {save.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <p className="text-xs text-fieldiq-muted">Only an admin or office staff can change pressure testing settings.</p>
        )}

        {!settings.configured && (
          <p className="text-xs text-fieldiq-muted" data-testid="pt-settings-defaults">
            These are the defaults. Nobody has saved pressure testing settings for this company yet.
          </p>
        )}
        {error && (
          <p role="alert" className="rounded-lg border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
            {error}
          </p>
        )}
        {warnings.map((w) => (
          <p key={w} role="status" className="rounded-lg border border-fi-signal-amber/40 bg-fi-signal-amber/10 px-3 py-2 text-sm text-fieldiq-text">
            {w}
          </p>
        ))}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label htmlFor="pt-cert-prefix" className="text-sm font-medium text-white block mb-1">Certificate prefix</label>
            <input
              id="pt-cert-prefix"
              className={inputCls}
              value={prefix}
              maxLength={20}
              disabled={!canWrite}
              onChange={(e) => setPrefix(e.target.value)}
            />
            <p className="text-xs text-fieldiq-muted mt-1">1 to 20 characters.</p>
          </div>
          <div>
            <label htmlFor="pt-cert-format" className="text-sm font-medium text-white block mb-1">Certificate format</label>
            <select
              id="pt-cert-format"
              className={inputCls}
              value={format}
              disabled={!canWrite}
              onChange={(e) => setFormat(e.target.value as PtCertFormat)}
            >
              {PT_CERT_FORMATS.map((f) => (
                <option key={f} value={f}>
                  {PT_CERT_FORMAT_LABEL[f]}
                </option>
              ))}
            </select>
            <p className="text-xs text-fieldiq-muted mt-1">ABSA is the Alberta registration format.</p>
          </div>
          <div>
            <label htmlFor="pt-warn-days" className="text-sm font-medium text-white block mb-1">Warning days</label>
            <input
              id="pt-warn-days"
              className={inputCls}
              value={warnDays}
              disabled={!canWrite}
              inputMode="numeric"
              onChange={(e) => setWarnDays(e.target.value)}
            />
            <p className="text-xs text-fieldiq-muted mt-1">
              Up to five, e.g. 60, 30, 7. The largest is when an instrument turns Due soon.
            </p>
          </div>
          <div>
            <label htmlFor="pt-default-ticket-mode" className="text-sm font-medium text-white block mb-1">Default ticket mode</label>
            <select
              id="pt-default-ticket-mode"
              className={inputCls}
              value={ticketMode}
              disabled={!canWrite}
              onChange={(e) => setTicketMode(e.target.value)}
              data-testid="pt-default-ticket-mode"
            >
              {!ticketMode && <option value="">—</option>}
              {PT_TICKET_MODES.map((m) => (
                <option key={m} value={m}>
                  {PT_TICKET_MODE_LABEL[m]}
                </option>
              ))}
            </select>
            <p className="text-xs text-fieldiq-muted mt-1">
              For a customer with no setting of its own. Per job: one field ticket at completion. Per day: one per work day.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
